import React, { forwardRef, useCallback, useImperativeHandle } from 'react'
import type { ReactNode } from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder/pquery-builder'
import { usePQueryBuilder } from '@pushly/aqe'

/**
 * What the builder knows about its own document at the moment a submit is asked
 * for.
 *
 * `supported: false` carries nothing else, and that is the point: a document the
 * builder cannot model does not serialize back to itself — it serializes to an
 * empty condition set — so there is no document here for a caller to mistake for
 * the user's, and no validity worth reporting about rows that were never parsed.
 */
export type BuilderSubmission =
    | { supported: false }
    | {
          supported: true
          valid: boolean
          conditionCount: number
          document: Record<string, unknown>
      }

/**
 * Imperative surface for a consumer that renders no footer of its own — the
 * drawer supplies its own buttons and drives the same submit through this.
 */
export interface SegmentBuilderFooterHandle {
    submit: () => Promise<boolean>
}

export interface SegmentBuilderFooterProps {
    readonly?: boolean
    /** A drawer supplies its own buttons, so the well renders none. */
    mode: 'standard' | 'drawer'
    /** True once a submit has been refused, which is when condition errors appear. */
    submitAttempted: boolean
    /**
     * Decides what to do with the submission and reports whether the segment was
     * saved. Resolving `false` means nothing was persisted.
     */
    onSubmit: (submission: BuilderSubmission) => Promise<boolean>
    onCancel: () => void
    /**
     * Rendered in the conditions well's own actions slot, beside the builder and
     * inside the same provider — so a sibling placed here reads the live document
     * through the builder without this component knowing what it is.
     */
    actions?: ReactNode
}

/**
 * The builder's conditions well and the submit control over it.
 *
 * This sits inside the provider so every fact a submit depends on is read from
 * the builder itself: whether the document is representable, whether its
 * conditions are complete, how many there are, and what it serializes to. Those
 * are reactive reads of the one place that knows, which is why a document that
 * arrives already invalid is refused as surely as one the user breaks — nothing
 * here waits to be told.
 */
export const SegmentBuilderFooter = forwardRef<SegmentBuilderFooterHandle, SegmentBuilderFooterProps>(
    ({ readonly, mode, submitAttempted, onSubmit, onCancel, actions }, ref) => {
        const { isValid, isSupported, conditionCount, serialize } = usePQueryBuilder()

        const submit = useCallback((): Promise<boolean> => {
            if (!isSupported) return onSubmit({ supported: false })

            return onSubmit({
                supported: true,
                valid: isValid,
                conditionCount,
                document: serialize(),
            })
        }, [onSubmit, isSupported, isValid, conditionCount, serialize])

        useImperativeHandle(ref, () => ({ submit }), [submit])

        return (
            <Well
                title="Conditions"
                actions={actions}
                footerMode={mode === 'drawer' ? 'hidden' : 'ghost'}
                onSubmit={submit}
                // The warning above tells the reader saving is off, so the control
                // that offers it has to be gone while that holds.
                hideSubmit={readonly || !isSupported}
                submitText="Save"
                onCancel={onCancel}
                cancelText="Cancel"
            >
                {/* A document the builder cannot represent is shown but not editable. Editing it
                    would offer changes that cannot be saved — serialization returns the stored
                    document, not the rows — so every edit would be silently discarded. */}
                <PQueryBuilder disabled={readonly || !isSupported} showErrors={submitAttempted && !isValid} />
            </Well>
        )
    },
)
